import { useState, type FormEvent } from 'react';
import { categoryFilter } from '@/mocks/homeContent';

type Status = { type: 'idle' | 'success' | 'error'; msg: string } | null;

const FORM_URL = 'https://readdy.ai/api/form/daej1m1tto0ceqqngu80';

export default function PostSection() {
  const [status, setStatus] = useState<Status>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾蜜罐字段
    const honey = (formData.get('website_alt') as string) || '';
    const cat = (formData.get('category') as string) || '';
    const title = (formData.get('title') as string) || '';
    const content = (formData.get('content') as string) || '';

    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      const v = typeof value === 'string' ? value.trim() : String(value);
      if (v) payload[key] = v;
    });

    // 客户端基础校验
    if (!title.trim() || !content.trim()) {
      setStatus({ type: 'error', msg: '请填写投稿标题和正文内容后再提交。' });
      return;
    }

    // 命中蜜罐：不真正发送，但给出与正常成功一致的反馈
    if (honey.trim()) {
      form.reset();
      setStatus({ type: 'success', msg: '投稿已收到，审核通过后将出现在对应栏目，感谢分享！' });
      return;
    }

    try {
      setSubmitting(true);
      const body = Object.keys(payload)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(payload[k])}`)
        .join('&');
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed?.meta as { message?: string })?.message ||
        (parsed?.message as string) ||
        (parsed?.meta as { detail?: string })?.detail ||
        responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const code = parsed?.code as string | undefined;

      if (response.ok && code === 'OK' && !isSpam) {
        form.reset();
        setStatus({ type: 'success', msg: '投稿已收到，审核通过后将出现在对应栏目，感谢分享！' });
      } else {
        setStatus({ type: 'error', msg: (typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。') || '提交失败，请稍后再试。' });
      }
    } catch {
      setStatus({ type: 'error', msg: '网络异常，请检查网络后重新提交。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="post" className="relative overflow-hidden bg-foreground-950 py-14 md:py-20">
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20editorial%20abstract%20background%20with%20coral%20red%20watercolor%20blobs%20and%20bright%20green%20citrus%20slices%20on%20deep%20charcoal%20paper%2C%20playful%20minimal%20illustration%2C%20soft%20grain%2C%20high%20contrast%20deep%20moody%20tones&width=1600&height=900&seq=chigua-cta-x2&orientation=landscape"
          alt="53吃瓜投稿背景装饰"
          className="h-full w-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/95 to-foreground-950/95"></div>

      <div className="relative mx-auto w-full max-w-5xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-accent-400">Send a Post</span>
          <h2 className="mt-1 font-heading text-3xl text-background-50 md:text-4xl">把你知道的新瓜分享给瓜友</h2>
          <p className="mt-2 text-sm leading-relaxed text-background-50/75">
            选题可以是热剧心得、综艺名场面、音乐新现场，或是生活里的趣事。填写表单，审核通过即刻发布。
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="mt-8 grid grid-cols-1 gap-4 rounded-xl border border-background-50/15 bg-background-50/95 p-5 sm:grid-cols-2 md:p-8"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="nickname" className="text-sm font-medium text-foreground-900">你的昵称</label>
            <input
              id="nickname"
              name="nickname"
              type="text"
              placeholder="怎么称呼你"
              autoComplete="nickname"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="contact" className="text-sm font-medium text-foreground-900">联系方式（邮箱）</label>
            <input
              id="contact"
              name="email"
              type="email"
              placeholder="用于接收审核结果"
              autoComplete="email"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="category" className="text-sm font-medium text-foreground-900">投稿栏目</label>
            <select
              id="category"
              name="category"
              defaultValue="娱乐新瓜"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
            >
              {categoryFilter.filter((c) => c !== '全部').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="title" className="text-sm font-medium text-foreground-900">投稿标题</label>
            <input
              id="title"
              name="title"
              type="text"
              required
              placeholder="用一句话概括你要分享的瓜或话题"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="content" className="text-sm font-medium text-foreground-900">投稿正文</label>
            <textarea
              id="content"
              name="content"
              required
              maxLength={500}
              rows={4}
              placeholder="把来龙去脉、看点和你自己的看法写下来，越具体瓜友越爱看（最多500字）"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>

          {/* 反垃圾蜜罐字段 */}
          <div className="sm:col-span-2">
            <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-400 disabled:opacity-60"
            >
              <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} text-lg`}></i>
              {submitting ? '提交中…' : '立即投稿发帖'}
            </button>
          </div>

          {status && (
            <div
              className={`sm:col-span-2 flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
                status.type === 'success' ? 'bg-accent-100 text-accent-900' : 'bg-primary-100 text-primary-900'
              }`}
            >
              <i className={`${status.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} mt-0.5 text-lg`}></i>
              <span>{status.msg}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}